import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import WorksheetRenderer from "@/components/WorksheetRenderer";
import { WORKSHEETS, GROUPS, STAGES, getWorksheet, computeStatus, scoreOf, likertBlock } from "@/config/worksheets";
import { STATUS_META, interpret } from "@/lib/status";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Check, Cloud, CloudOff, Loader2, Printer, Stethoscope, Flag, LayoutDashboard } from "lucide-react";
import { toast } from "sonner";

export default function AssessmentEditorPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [assessment, setAssessment] = useState(null);
  const [responses, setResponses] = useState({});
  const [activeId, setActiveId] = useState("w01");
  const [saveState, setSaveState] = useState("idle"); // idle|saving|saved|offline
  const [lastSaved, setLastSaved] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [consult, setConsult] = useState("");
  const [showConsult, setShowConsult] = useState(false);
  const [, tick] = useState(0);

  const timer = useRef(null);
  const pending = useRef(null);

  useEffect(() => {
    api.get(`/assessments/${id}`).then((r) => {
      setAssessment(r.data);
      setResponses(r.data.responses || {});
      setConsult(r.data.consultation?.notes || "");
      const wanted = params.get("w");
      setActiveId(getWorksheet(wanted) ? wanted : r.data.last_worksheet || "w01");
    }).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); nav("/"); });
    // Reload only when the assessment id changes; setters/nav are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // re-render "saved x ago" every 5s
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setInterval(() => tick((x) => x + 1), 5000); return () => clearInterval(t); }, []);

  const ws = getWorksheet(activeId);
  const data = responses[activeId]?.data || {};

  const loadDashboard = useCallback(() => {
    api.get(`/assessments/${id}/dashboard`).then((r) => setDashboardData(r.data)).catch(() => {});
  }, [id]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (ws?.dashboard) loadDashboard(); }, [activeId, ws, loadDashboard]);

  const persist = useCallback(async (wid, newData) => {
    const w = getWorksheet(wid);
    const status = computeStatus(w, newData);
    setSaveState("saving");
    pending.current = { wid, newData, status };
    try {
      await api.put(`/assessments/${id}/worksheets/${wid}`, { data: newData, status });
      pending.current = null;
      setSaveState("saved");
      setLastSaved(Date.now());
    } catch (e) {
      setSaveState("offline");
      // retry after delay
      setTimeout(() => { if (pending.current) persist(pending.current.wid, pending.current.newData); }, 3000);
    }
  }, [id]);

  const onChange = (key, value, immediate) => {
    const nd = { ...data, [key]: value };
    setResponses((prev) => ({ ...prev, [activeId]: { ...(prev[activeId] || {}), data: nd } }));
    if (timer.current) clearTimeout(timer.current);
    const delay = immediate ? 0 : 700;
    timer.current = setTimeout(() => persist(activeId, nd), delay);
  };

  const goTo = (wid) => {
    if (wid === activeId) return;
    if (timer.current) { clearTimeout(timer.current); persist(activeId, responses[activeId]?.data || {}); }
    setActiveId(wid);
    api.patch(`/assessments/${id}`, { last_worksheet: wid }).catch(() => {});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const idx = WORKSHEETS.findIndex((w) => w.id === activeId);
  const prevW = WORKSHEETS[idx - 1];
  const nextW = WORKSHEETS[idx + 1];

  const saveContinue = async () => {
    const nd = { ...data, __complete: true };
    setResponses((prev) => ({ ...prev, [activeId]: { ...(prev[activeId] || {}), data: nd } }));
    await persist(activeId, nd);
    toast.success(t("Saved"));
    if (nextW) goTo(nextW.id);
  };

  const flagReview = async () => {
    const nd = { ...data, __review: true };
    setResponses((prev) => ({ ...prev, [activeId]: { ...(prev[activeId] || {}), data: nd } }));
    await persist(activeId, nd);
    toast.success(t("Flagged for review"));
  };

  const saveConsult = async () => {
    try { await api.patch(`/assessments/${id}/consultation`, { notes: consult }); toast.success(t("Consultation saved")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const statusFor = (wid) => {
    const r = responses[wid];
    if (r?.status === "needs_review") return "needs_review";
    return computeStatus(getWorksheet(wid), r?.data || {});
  };

  if (!assessment) return <AppShell><p className="text-sm text-stone-400">Loading…</p></AppShell>;

  const lb = ws?.scored ? likertBlock(ws) : null;
  const liveScore = lb ? scoreOf(ws, data) : null;
  const answeredCount = lb ? lb.statements.filter((_, i) => data["s" + i] === "na" || typeof data["s" + i] === "number").length : 0;
  const isDoctor = user?.role === "doctor" || user?.role === "admin";
  const isAdmin = user?.role === "admin";

  const changeStage = async (newStage) => {
    if (!assessment || newStage === assessment.stage) return;
    const prev = assessment.stage;
    setAssessment((a) => ({ ...a, stage: newStage }));
    try {
      await api.patch(`/assessments/${id}`, { stage: newStage });
      toast.success(t("Stage updated"));
    } catch (e) {
      setAssessment((a) => ({ ...a, stage: prev }));
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <AppShell right={<SaveIndicator state={saveState} lastSaved={lastSaved} t={t} />}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <button onClick={() => nav("/")} className="mb-1 flex items-center gap-1 text-xs text-stone-500 hover:text-emerald-700" data-testid="back-btn">
            <ChevronLeft className="h-3 w-3" /> {t("All submissions")}
          </button>
          <h1 className="font-serif text-2xl font-bold text-stone-900">{t(assessment.title)}</h1>
          <p className="text-xs text-stone-500">{t(assessment.stage)} · {assessment.participant_name}</p>
        </div>
        <div className="flex gap-2">
          {isDoctor && (
            <button onClick={() => nav(`/assessment/${id}/clinical`)} data-testid="open-clinical-btn"
              className="flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 hover:bg-emerald-100">
              <LayoutDashboard className="h-4 w-4" /> {t("Clinical snapshot")}
            </button>
          )}
          {isDoctor && (
            <button onClick={() => setShowConsult((s) => !s)} data-testid="consult-toggle"
              className="flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 hover:bg-emerald-100">
              <Stethoscope className="h-4 w-4" /> {t("Consultation")}
            </button>
          )}
          <button onClick={() => window.open(`/print/${id}`, "_blank")} data-testid="download-pdf-btn"
            className="flex items-center gap-1.5 rounded-md bg-emerald-800 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-900">
            <Printer className="h-4 w-4" /> {t("Download PDF")}
          </button>
        </div>
      </div>

      {showConsult && isDoctor && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50/60 p-4" data-testid="consult-panel">
          <h4 className="mb-2 flex items-center gap-2 font-semibold text-emerald-900"><Stethoscope className="h-4 w-4" /> {t("Doctor consultation notes")}</h4>
          <textarea value={consult} onChange={(e) => setConsult(e.target.value)} data-testid="consult-notes"
            className="w-full rounded-md border border-emerald-200 bg-white p-3 text-sm" rows={4} placeholder="Clinical observations, recommendations, dinacharya plan…" />
          <button onClick={saveConsult} data-testid="consult-save" className="mt-2 rounded-md bg-emerald-800 px-4 py-1.5 text-sm font-semibold text-white hover:bg-emerald-900">{t("Save notes")}</button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr]">
        {/* sidebar */}
        <aside className="no-print lg:sticky lg:top-20 lg:self-start">
          <div className="rounded-lg border border-[#E5DFD3] bg-white p-3">
            <div className="mb-2 px-1 text-xs font-mono uppercase tracking-wider text-stone-400">{t("Worksheets")}</div>
            <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1">
              {GROUPS.map((g) => {
                const list = WORKSHEETS.filter((w) => w.group === g);
                if (!list.length) return null;
                return (
                  <div key={g}>
                    <div className="px-1 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-800/70">{t(g)}</div>
                    {list.map((w) => {
                      const st = STATUS_META[statusFor(w.id)];
                      return (
                        <button key={w.id} onClick={() => goTo(w.id)} data-testid={`nav-${w.id}`}
                          className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition ${w.id === activeId ? "bg-[#E8EFE9] font-medium text-emerald-900" : "text-stone-600 hover:bg-stone-50"}`}>
                          <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: st.dot }} />
                          <span className="font-mono text-xs text-stone-400">{w.num}</span>
                          <span className="truncate">{t(w.title).split(":")[0]}</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </aside>

        {/* worksheet paper */}
        <section>
          <div className="ws-paper rounded-lg p-6 sm:p-8">
            <div className="mb-1 text-xs font-mono uppercase tracking-widest text-emerald-800/70">Ayurtoday Ayurveda · Worksheet {ws.num}</div>
            <h2 className="font-serif text-2xl font-bold text-stone-900">{t(ws.title)}</h2>
            <div className="mt-3 mb-6 flex flex-wrap gap-x-6 gap-y-1 border-b border-dashed border-[#E5DFD3] pb-4 text-sm text-stone-600">
              <span>{t("Participant")}: <b className="text-stone-800">{assessment.participant_name}</b></span>
              <span className="inline-flex items-center gap-1">
                {t("Stage")}:{" "}
                {isAdmin ? (
                  <select value={assessment.stage} onChange={(e) => changeStage(e.target.value)} data-testid="stage-select"
                    className="no-print rounded-md border border-[#E5DFD3] bg-white px-2 py-0.5 text-sm font-semibold text-stone-800 focus:border-emerald-600 focus:outline-none">
                    {STAGES.map((s) => <option key={s} value={s}>{t(s)}</option>)}
                  </select>
                ) : (
                  <b className="text-stone-800">{t(assessment.stage)}</b>
                )}
              </span>
              <span>{t("Date")}: <b className="text-stone-800">{new Date().toLocaleDateString()}</b></span>
            </div>

            <WorksheetRenderer ws={ws} data={data} onChange={onChange} dashboardData={dashboardData} />

            {lb && (
              <ScoreBox score={liveScore} answered={answeredCount} total={lb.statements.length} t={t} />
            )}
          </div>

          {/* nav bar */}
          <div className="no-print mt-4 flex items-center justify-between gap-3 rounded-lg border border-[#E5DFD3] bg-white px-4 py-3">
            <button disabled={!prevW} onClick={() => prevW && goTo(prevW.id)} data-testid="prev-btn"
              className="flex items-center gap-1 rounded-md border border-[#E5DFD3] px-3 py-2 text-sm text-stone-600 disabled:opacity-40 hover:bg-stone-50">
              <ChevronLeft className="h-4 w-4" /> {t("Previous")}
            </button>
            <div className="flex items-center gap-2">
              {isDoctor && (
                <button onClick={flagReview} data-testid="flag-review-btn"
                  className="flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 hover:bg-amber-100">
                  <Flag className="h-4 w-4" /> {t("Flag review")}
                </button>
              )}
              <button onClick={saveContinue} data-testid="save-continue-btn"
                className="flex items-center gap-1.5 rounded-md bg-emerald-800 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-900">
                <Check className="h-4 w-4" /> {t("Save & Continue")}
              </button>
            </div>
            <button disabled={!nextW} onClick={() => nextW && goTo(nextW.id)} data-testid="next-btn"
              className="flex items-center gap-1 rounded-md border border-[#E5DFD3] px-3 py-2 text-sm text-stone-600 disabled:opacity-40 hover:bg-stone-50">
              {t("Next")} <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </section>
      </div>
    </AppShell>
  );
}

function ScoreBox({ score, answered, total, t }) {
  const it = interpret(score);
  return (
    <div className={`mt-8 rounded-lg border p-5 ${it.bg}`} data-testid="score-box" style={{ borderColor: it.color + "55" }}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-stone-500">{t("Domain score / 10")}</div>
          <div className="font-serif text-4xl font-bold" style={{ color: it.color }} data-testid="domain-score">{score ?? "—"}</div>
          <div className="text-xs text-stone-500">{answered}/{total} {t("statements answered")} · {t("formula (points ÷ maximum) × 10")}</div>
        </div>
        <span className={`rounded-full px-3 py-1 text-sm font-medium ${it.badge}`} style={{ backgroundColor: "white" }}>{t(it.label)}</span>
      </div>
    </div>
  );
}

function SaveIndicator({ state, lastSaved, t }) {
  let icon, text, cls;
  if (state === "saving") { icon = <Loader2 className="h-3.5 w-3.5 animate-spin" />; text = t("Saving…"); cls = "text-amber-700 bg-amber-50 border-amber-200"; }
  else if (state === "offline") { icon = <CloudOff className="h-3.5 w-3.5" />; text = t("Offline · will retry"); cls = "text-rose-700 bg-rose-50 border-rose-200"; }
  else if (state === "saved" || lastSaved) {
    const secs = lastSaved ? Math.round((Date.now() - lastSaved) / 1000) : 0;
    const ago = secs < 5 ? t("just now") : secs < 60 ? `${secs}s` : `${Math.round(secs / 60)}m`;
    icon = <Check className="h-3.5 w-3.5" />; text = secs < 5 ? t("Saved just now") : t("Saved {{ago}}", { ago }); cls = "text-emerald-700 bg-emerald-50 border-emerald-200";
  } else { icon = <Cloud className="h-3.5 w-3.5" />; text = t("All changes saved"); cls = "text-stone-500 bg-stone-50 border-stone-200"; }
  return (
    <div className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${cls}`} data-testid="save-indicator">
      {icon}{text}
    </div>
  );
}
